// PRD 05 §3.3 — type-specific edit UIs for the slide-panel. Each editor
// receives the relevant slice of the draft + a setter, and renders the
// matching input grid. The wrapper component (`AnswerEditor`) picks one by
// `answerType`.
//
// Default-config factories ensure the panel never crashes if an answer type
// is selected before its config exists in the draft.

import { useTranslation } from 'react-i18next';
import { Plus, TextCursorInput, X } from 'lucide-react';

import { NumberInput } from '@/components/forms/NumberInput';

import type {
  AnswerType,
  BatteryConfig,
  Choice,
  MultiSelectConfig,
  PhotoConfig,
  SliderConfig,
  TextConfig,
} from '../types/questionnaire-design.types';

// ── Default factories ───────────────────────────────────────────────────

export function defaultChoicesFor(type: AnswerType): Choice[] | undefined {
  switch (type) {
    case 'YES_NO':
      return [
        { text: '예', score: 0, branch: { kind: 'NEXT' } },
        { text: '아니오', score: 0, branch: { kind: 'NEXT' } },
      ];
    case 'OX':
      return [
        { text: 'O', score: 5, branch: { kind: 'NEXT' }, correct: true },
        { text: 'X', score: 0, branch: { kind: 'NEXT' }, correct: false },
      ];
    case 'SHORT_ANSWER':
    case 'MULTI_CHOICE':
      return [
        { text: '', score: 0, branch: { kind: 'NEXT' } },
        { text: '', score: 0, branch: { kind: 'NEXT' } },
      ];
    default:
      return undefined;
  }
}

export const defaultMultiSelectConfig = (): MultiSelectConfig => ({
  mode: 'none',
  min: 1,
  max: 2,
});
// PRD 05 §textConfig (v1.20, 2026-07-08) — maxChars default raised 200 → 500 so
// the formal 텍스트 입력형 type matches the '기타' free-text cap. Prototype
// `survey-editor.html` uses 500 as well.
export const defaultTextConfig = (): TextConfig => ({ minChars: 1, maxChars: 500, score: 1 });
export const defaultSliderConfig = (): SliderConfig => ({ min: 0, max: 10, step: 1, unit: '점' });
// PRD 05 §photoConfig — maxFiles default 1, maxSizeMB default 5 (prototype
// `survey-editor.html` matches). The old 3 / 10 defaults pre-dated the ADM.041
// 5MB ceiling, so a fresh 사진첨부 question defaulted to an unsavable 10MB.
export const defaultPhotoConfig = (): PhotoConfig => ({ maxFiles: 1, maxSizeMB: 5 });
export const defaultBatteryConfig = (): BatteryConfig => ({
  scaleLabels: ['전혀 그렇지 않다', '그렇지 않다', '그렇다', '매우 그렇다'],
  scaleScores: [0, 1, 2, 3],
  categories: [
    {
      name: '카테고리 1',
      items: [{ text: '새 문항', allowPhoto: false }],
    },
  ],
});

// ── Editor: choice-based (SHORT_ANSWER, MULTI_CHOICE, YES_NO) ────────────

// Options the JUMP target dropdown can show. The QuestionEditPanel filters
// eligible siblings using the bucket rule (same target + same parentQid +
// strictly later order) and feeds them here as `[qid, label]` pairs so this
// editor stays presentational.
export interface JumpTargetOption {
  qid: string;
  // Display "N" or "N-M" the user already sees on the destination card.
  label: string;
}

// SHCT-851 — flags a score that falls outside the question's 최저점~최고점 band so
// the offending input is red-ringed rather than only named in the answer-area
// message. Supplied by the panel from the same predicate the save check uses.
type ScoreOutOfBand = (score: unknown) => boolean;

interface ChoiceTableEditorProps {
  choices: Choice[];
  setChoices: (next: Choice[]) => void;
  showBranch: boolean;
  // Yes/No has fixed text — score + branch only.
  fixedText: boolean;
  // Hide the "+ 기타" button for Yes/No.
  allowOther: boolean;
  jumpTargets: JumpTargetOption[];
  scoreOutOfBand: ScoreOutOfBand;
}

function ChoiceTableEditor({
  choices,
  setChoices,
  showBranch,
  fixedText,
  allowOther,
  jumpTargets,
  scoreOutOfBand,
}: ChoiceTableEditorProps) {
  const { t } = useTranslation('questionnaire-design');
  const hasOther = choices.some((c) => c.chType === 'other');

  const update = (idx: number, patch: Partial<Choice>) => {
    setChoices(choices.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };
  const remove = (idx: number) => {
    const next = choices.filter((_, i) => i !== idx);
    setChoices(
      next.length
        ? next
        : [{ text: '', score: 0, branch: { kind: 'NEXT' } }],
    );
  };
  const add = () => {
    setChoices([...choices, { text: '', score: 0, branch: { kind: 'NEXT' } }]);
  };
  const addOther = () => {
    if (hasOther) return;
    setChoices([
      ...choices,
      {
        text: '기타 (텍스트입력)',
        score: 0,
        branch: { kind: 'NEXT' },
        chType: 'other',
      },
    ]);
  };

  return (
    <>
      {!fixedText ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
            gap: 6,
          }}
        >
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--muted-foreground)' }}>
            {showBranch
              ? t('answerEditor.shortAnswerHint')
              : t('answerEditor.multiChoiceHint')}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            {allowOther ? (
              <button
                type="button"
                className="btn btn-sm btn-default"
                onClick={addOther}
                disabled={hasOther}
                title={
                  hasOther
                    ? t('answerEditor.otherDisabledTitle')
                    : undefined
                }
                style={{ padding: '4px 10px', fontSize: 12 }}
              >
                <TextCursorInput size={14} />{' '}
                {t('answerEditor.addOther')}
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-sm btn-default"
              onClick={add}
              style={{ padding: '4px 10px', fontSize: 12 }}
            >
              <Plus size={14} /> {t('answerEditor.addChoice')}
            </button>
          </div>
        </div>
      ) : (
        <div
          style={{
            marginBottom: 8,
            fontSize: 'var(--font-size-xs)',
            color: 'var(--muted-foreground)',
          }}
        >
          {t('answerEditor.yesNoHint')}
        </div>
      )}

      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
        <table className="table" style={{ margin: 0, fontSize: 'var(--font-size-xs)' }}>
          <thead>
            <tr>
              <th style={{ padding: '6px 8px' }}>
                {t('answerEditor.columns.text')}
              </th>
              <th style={{ width: 70, padding: '6px 8px' }}>
                {t('answerEditor.columns.score')}
              </th>
              {showBranch ? (
                <th style={{ width: 160, padding: '6px 8px' }}>
                  {t('answerEditor.columns.branch')}
                </th>
              ) : null}
              {!fixedText ? (
                <th style={{ width: 44, textAlign: 'center', padding: '6px 8px' }}>
                  {t('answerEditor.columns.delete')}
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {choices.map((choice, idx) => (
              <tr key={idx}>
                <td style={{ padding: '4px 6px' }}>
                  {fixedText ? (
                    <span style={{ fontSize: 12, fontWeight: 500 }}>{choice.text}</span>
                  ) : choice.chType === 'other' ? (
                    // PRD 05 §3.3 — the "기타 (텍스트입력)" label is fixed; the admin
                    // cannot edit it (only delete). The empty/500-char rules apply to
                    // the patient's runtime response (otherText), not admin authoring.
                    <span style={{ color: 'var(--info)', fontWeight: 500, fontSize: 12 }}>
                      <TextCursorInput
                        size={12}
                        style={{ verticalAlign: 'middle', marginRight: 4 }}
                      />
                      {t('answerDisplay.otherLabel')}
                    </span>
                  ) : (
                    <input
                      type="text"
                      className="form-field__input"
                      value={choice.text}
                      onChange={(e) => update(idx, { text: e.target.value })}
                      placeholder={t('answerEditor.choicePlaceholder')}
                      style={{ height: 30, fontSize: 12 }}
                    />
                  )}
                </td>
                <td style={{ padding: '4px 6px' }}>
                  <NumberInput
                    className={`form-field__input${
                      scoreOutOfBand(choice.score) ? ' form-field__input--error' : ''
                    }`}
                    value={choice.score}
                    min={0}
                    step={1}
                    onChange={(score) => update(idx, { score })}
                    style={{ height: 30, fontSize: 12, textAlign: 'right' }}
                  />
                </td>
                {showBranch ? (
                  <td style={{ padding: '4px 6px' }}>
                    <div style={{ display: 'flex', gap: 4, flexDirection: 'column' }}>
                      <select
                        className="form-field__select"
                        value={choice.branch?.kind ?? 'NEXT'}
                        onChange={(e) => {
                          const nextKind = e.target.value as 'NEXT' | 'END' | 'JUMP';
                          // Selecting JUMP without an eligible sibling would
                          // strand the picker; default the targetQid to the
                          // first eligible row so the save path can validate.
                          // Same answer for NEXT/END: clear targetQid.
                          const nextBranch =
                            nextKind === 'JUMP'
                              ? {
                                  kind: 'JUMP' as const,
                                  targetQid:
                                    choice.branch?.kind === 'JUMP'
                                      ? choice.branch.targetQid
                                      : jumpTargets[0]?.qid,
                                }
                              : { kind: nextKind };
                          update(idx, { branch: nextBranch });
                        }}
                        style={{
                          height: 30,
                          fontSize: 12,
                          padding: '0 6px',
                          background:
                            (choice.branch?.kind ?? 'NEXT') === 'NEXT'
                              ? '#E6EEFF'
                              : choice.branch?.kind === 'END'
                                ? '#FEE2E8'
                                : '#F0E6FF',
                        }}
                      >
                        <option value="NEXT">
                          {t('answerEditor.branchKindNext')}
                        </option>
                        <option value="END">
                          {t('answerEditor.branchKindEnd')}
                        </option>
                        <option
                          value="JUMP"
                          disabled={jumpTargets.length === 0}
                        >
                          {t('answerEditor.branchKindJump')}
                        </option>
                      </select>
                      {choice.branch?.kind === 'JUMP' ? (
                        <select
                          className="form-field__select"
                          value={choice.branch.targetQid ?? ''}
                          onChange={(e) =>
                            update(idx, {
                              branch: {
                                kind: 'JUMP',
                                targetQid: e.target.value || undefined,
                              },
                            })
                          }
                          style={{
                            height: 28,
                            fontSize: 11,
                            padding: '0 6px',
                            background: '#F0E6FF',
                          }}
                          aria-label={t('answerEditor.branchJumpAria')}
                        >
                          {jumpTargets.length === 0 ? (
                            <option value="">
                              {t('answerEditor.branchJumpEmpty')}
                            </option>
                          ) : (
                            jumpTargets.map((opt) => (
                              <option key={opt.qid} value={opt.qid}>
                                {t('answerEditor.branchJumpOption', {
                                  num: opt.label,
                                })}
                              </option>
                            ))
                          )}
                        </select>
                      ) : null}
                    </div>
                  </td>
                ) : null}
                {!fixedText ? (
                  <td style={{ textAlign: 'center', padding: '4px 6px' }}>
                    <button
                      type="button"
                      onClick={() => remove(idx)}
                      style={{
                        padding: '2px 6px',
                        background: 'var(--muted)',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 12,
                        borderRadius: 4,
                      }}
                      aria-label={t('answerEditor.deleteAria')}
                    >
                      <X size={12} />
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Editor: OX ──────────────────────────────────────────────────────────

function OxEditor({
  choices,
  setChoices,
  scoreOutOfBand,
}: {
  choices: Choice[];
  setChoices: (next: Choice[]) => void;
  scoreOutOfBand: ScoreOutOfBand;
}) {
  const { t } = useTranslation('questionnaire-design');
  const updateScore = (idx: number, score: number) => {
    setChoices(choices.map((c, i) => (i === idx ? { ...c, score } : c)));
  };
  const setCorrect = (idx: number) => {
    setChoices(choices.map((c, i) => ({ ...c, correct: i === idx })));
  };
  return (
    <>
      <div style={{ marginBottom: 8, fontSize: 'var(--font-size-xs)', color: 'var(--muted-foreground)' }}>
        {t('answerEditor.oxHint')}
      </div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
        <table className="table" style={{ margin: 0, fontSize: 'var(--font-size-xs)' }}>
          <thead>
            <tr>
              <th style={{ padding: '6px 8px' }}>
                {t('answerEditor.columns.choice')}
              </th>
              <th style={{ width: 60, textAlign: 'center', padding: '6px 8px' }}>
                {t('answerEditor.columns.correct')}
              </th>
              <th style={{ width: 80, padding: '6px 8px' }}>
                {t('answerEditor.columns.score')}
              </th>
            </tr>
          </thead>
          <tbody>
            {choices.map((c, idx) => (
              <tr key={idx}>
                <td style={{ padding: '6px 8px', fontWeight: 500 }}>{c.text}</td>
                <td style={{ textAlign: 'center', padding: '4px 6px' }}>
                  <input
                    type="radio"
                    name="seOxCorrect"
                    checked={!!c.correct}
                    onChange={() => setCorrect(idx)}
                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                    aria-label={t('answerEditor.correctAria', { text: c.text })}
                  />
                </td>
                <td style={{ padding: '4px 6px' }}>
                  <NumberInput
                    className={`form-field__input${
                      scoreOutOfBand(c.score) ? ' form-field__input--error' : ''
                    }`}
                    value={c.score}
                    min={0}
                    onChange={(score) => updateScore(idx, score)}
                    style={{ height: 30, fontSize: 12, textAlign: 'right' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Editor: TEXT ────────────────────────────────────────────────────────

function TextEditor({
  cfg,
  setCfg,
  scoreOutOfBand,
}: {
  cfg: TextConfig;
  setCfg: (next: TextConfig) => void;
  scoreOutOfBand: ScoreOutOfBand;
}) {
  const { t } = useTranslation('questionnaire-design');
  return (
    <>
      <div
        style={{
          border: '1px dashed var(--border)',
          borderRadius: 'var(--radius-sm)',
          padding: 'var(--space-3)',
          marginBottom: 'var(--space-3)',
          color: 'var(--muted-foreground)',
        }}
      >
        <div style={{ fontSize: 'var(--font-size-xs)', marginBottom: 6 }}>
          {t('answerEditor.text.preview')}
        </div>
        <input
          type="text"
          className="form-field__input"
          disabled
          placeholder={t('answerEditor.text.placeholder')}
          style={{ width: '100%' }}
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-3)' }}>
        <div className="form-field">
          <label className="form-field__label" style={{ fontSize: 11 }}>
            {t('answerEditor.text.minChars')}
          </label>
          <NumberInput
            className="form-field__input"
            value={cfg.minChars}
            min={1}
            max={500}
            emptyValue={1}
            onChange={(minChars) => setCfg({ ...cfg, minChars })}
          />
        </div>
        <div className="form-field">
          <label className="form-field__label" style={{ fontSize: 11 }}>
            {t('answerEditor.text.maxChars')}
          </label>
          <NumberInput
            className="form-field__input"
            value={cfg.maxChars}
            min={1}
            max={500}
            emptyValue={1}
            onChange={(maxChars) => setCfg({ ...cfg, maxChars })}
          />
        </div>
        <div className="form-field">
          <label className="form-field__label" style={{ fontSize: 11 }}>
            {t('answerEditor.text.score')}
          </label>
          <NumberInput
            className={`form-field__input${
              scoreOutOfBand(cfg.score) ? ' form-field__input--error' : ''
            }`}
            value={cfg.score}
            min={0}
            max={100}
            onChange={(score) => setCfg({ ...cfg, score })}
          />
        </div>
      </div>
    </>
  );
}

// ── Editor: SLIDER ──────────────────────────────────────────────────────

function SliderEditor({
  cfg,
  setCfg,
  scoreOutOfBand,
}: {
  cfg: SliderConfig;
  setCfg: (next: SliderConfig) => void;
  scoreOutOfBand: ScoreOutOfBand;
}) {
  const { t } = useTranslation('questionnaire-design');
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-3)' }}>
        <div className="form-field">
          <label className="form-field__label" style={{ fontSize: 11 }}>
            {t('answerEditor.slider.min')}
          </label>
          <NumberInput
            className={`form-field__input${
              scoreOutOfBand(cfg.min) ? ' form-field__input--error' : ''
            }`}
            value={cfg.min}
            onChange={(min) => setCfg({ ...cfg, min })}
          />
        </div>
        <div className="form-field">
          <label className="form-field__label" style={{ fontSize: 11 }}>
            {t('answerEditor.slider.max')}
          </label>
          <NumberInput
            className={`form-field__input${
              scoreOutOfBand(cfg.max) ? ' form-field__input--error' : ''
            }`}
            value={cfg.max}
            emptyValue={10}
            onChange={(max) => setCfg({ ...cfg, max })}
          />
        </div>
        <div className="form-field">
          <label className="form-field__label" style={{ fontSize: 11 }}>
            {t('answerEditor.slider.step')}
          </label>
          <NumberInput
            className="form-field__input"
            value={cfg.step}
            min={1}
            emptyValue={1}
            onChange={(step) => setCfg({ ...cfg, step })}
          />
        </div>
        <div className="form-field">
          <label className="form-field__label" style={{ fontSize: 11 }}>
            {t('answerEditor.slider.unit')}
          </label>
          <input
            type="text"
            className="form-field__input"
            value={cfg.unit}
            maxLength={10}
            onChange={(e) => setCfg({ ...cfg, unit: e.target.value || '점' })}
          />
        </div>
      </div>
      <div style={{ marginTop: 8, fontSize: 'var(--font-size-xs)', color: 'var(--muted-foreground)' }}>
        {t('answerEditor.slider.help')}
      </div>
    </>
  );
}

// ── Editor: PHOTO ───────────────────────────────────────────────────────

function PhotoEditor({
  cfg,
  setCfg,
}: {
  cfg: PhotoConfig;
  setCfg: (next: PhotoConfig) => void;
}) {
  const { t } = useTranslation('questionnaire-design');
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <div className="form-field">
          <label className="form-field__label" style={{ fontSize: 11 }}>
            {t('answerEditor.photo.maxFiles')}
          </label>
          <NumberInput
            className="form-field__input"
            value={cfg.maxFiles}
            min={1}
            max={9}
            emptyValue={1}
            onChange={(maxFiles) => setCfg({ ...cfg, maxFiles })}
          />
        </div>
        <div className="form-field">
          <label className="form-field__label" style={{ fontSize: 11 }}>
            {t('answerEditor.photo.maxSize')}
          </label>
          <NumberInput
            className="form-field__input"
            value={cfg.maxSizeMB}
            min={1}
            max={5}
            emptyValue={1}
            onChange={(maxSizeMB) => setCfg({ ...cfg, maxSizeMB })}
          />
        </div>
      </div>
      <div style={{ marginTop: 8, fontSize: 'var(--font-size-xs)', color: 'var(--muted-foreground)' }}>
        {t('answerEditor.photo.help')}
      </div>
    </>
  );
}

// ── Editor: BATTERY ─────────────────────────────────────────────────────

function BatteryEditor({
  cfg,
  setCfg,
  scoreOutOfBand,
}: {
  cfg: BatteryConfig;
  setCfg: (next: BatteryConfig) => void;
  scoreOutOfBand: ScoreOutOfBand;
}) {
  const { t } = useTranslation('questionnaire-design');
  const setLabel = (idx: number, value: string) => {
    const next = [...cfg.scaleLabels];
    next[idx] = value;
    setCfg({ ...cfg, scaleLabels: next });
  };
  const setScore = (idx: number, value: number) => {
    const next = [...cfg.scaleScores];
    next[idx] = value;
    setCfg({ ...cfg, scaleScores: next });
  };
  const renameCategory = (catIdx: number, name: string) => {
    setCfg({
      ...cfg,
      categories: cfg.categories.map((c, i) => (i === catIdx ? { ...c, name } : c)),
    });
  };
  const removeCategory = (catIdx: number) => {
    setCfg({
      ...cfg,
      categories: cfg.categories.filter((_, i) => i !== catIdx),
    });
  };
  const addCategory = () => {
    setCfg({
      ...cfg,
      categories: [
        ...cfg.categories,
        { name: '새 카테고리', items: [{ text: '새 문항', allowPhoto: false }] },
      ],
    });
  };
  const renameItem = (catIdx: number, itemIdx: number, text: string) => {
    setCfg({
      ...cfg,
      categories: cfg.categories.map((c, ci) =>
        ci === catIdx
          ? { ...c, items: c.items.map((it, ii) => (ii === itemIdx ? { ...it, text } : it)) }
          : c,
      ),
    });
  };
  const toggleItemPhoto = (catIdx: number, itemIdx: number, allowPhoto: boolean) => {
    setCfg({
      ...cfg,
      categories: cfg.categories.map((c, ci) =>
        ci === catIdx
          ? { ...c, items: c.items.map((it, ii) => (ii === itemIdx ? { ...it, allowPhoto } : it)) }
          : c,
      ),
    });
  };
  const removeItem = (catIdx: number, itemIdx: number) => {
    setCfg({
      ...cfg,
      categories: cfg.categories.map((c, ci) =>
        ci === catIdx ? { ...c, items: c.items.filter((_, ii) => ii !== itemIdx) } : c,
      ),
    });
  };
  const addItem = (catIdx: number) => {
    setCfg({
      ...cfg,
      categories: cfg.categories.map((c, ci) =>
        ci === catIdx
          ? { ...c, items: [...c.items, { text: '새 문항', allowPhoto: false }] }
          : c,
      ),
    });
  };

  return (
    <>
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-3)',
          marginBottom: 'var(--space-3)',
          background: '#f8fbff',
        }}
      >
        <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, marginBottom: 8 }}>
          {t('answerEditor.battery.scaleLabels')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-2)', marginBottom: 8 }}>
          {cfg.scaleLabels.map((label, i) => (
            <input
              key={i}
              type="text"
              className="form-field__input"
              value={label}
              maxLength={20}
              onChange={(e) => setLabel(i, e.target.value)}
              style={{ height: 30, fontSize: 12 }}
            />
          ))}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, marginBottom: 8 }}>
          {t('answerEditor.battery.scaleScores')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-2)' }}>
          {cfg.scaleScores.map((score, i) => (
            <NumberInput
              key={i}
              className={`form-field__input${
                scoreOutOfBand(score) ? ' form-field__input--error' : ''
              }`}
              value={score}
              onChange={(next) => setScore(i, next)}
              style={{ height: 30, fontSize: 12 }}
            />
          ))}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--muted-foreground)' }}>
          {t('answerEditor.battery.categoryCount', {
            count: cfg.categories.length,
          })}
        </span>
        <button
          type="button"
          className="btn btn-sm btn-default"
          onClick={addCategory}
          style={{ padding: '4px 10px', fontSize: 12 }}
        >
          <Plus size={14} /> {t('answerEditor.battery.addCategory')}
        </button>
      </div>

      {cfg.categories.map((cat, catIdx) => (
        <details
          key={catIdx}
          open
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-3)',
            overflow: 'hidden',
          }}
        >
          <summary
            style={{
              listStyle: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              padding: 'var(--space-2) var(--space-3)',
              cursor: 'pointer',
              background: 'var(--accent)',
            }}
          >
            <input
              type="text"
              className="form-field__input"
              value={cat.name}
              maxLength={20}
              onChange={(e) => renameCategory(catIdx, e.target.value)}
              onClick={(e) => e.preventDefault()}
              style={{ height: 28, fontSize: 12, maxWidth: 280 }}
            />
            <span style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
              {t('answerEditor.battery.itemCount', {
                count: cat.items.length,
              })}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                removeCategory(catIdx);
              }}
              style={{
                padding: '2px 6px',
                background: 'var(--muted)',
                border: 'none',
                cursor: 'pointer',
                fontSize: 11,
                borderRadius: 4,
                color: 'var(--destructive)',
              }}
            >
              {t('answerEditor.battery.remove')}
            </button>
          </summary>
          <div style={{ padding: 'var(--space-3)' }}>
            {cat.items.map((item, itemIdx) => (
              <div key={itemIdx} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <textarea
                    className="form-field__textarea"
                    rows={2}
                    value={item.text}
                    maxLength={50}
                    onChange={(e) => renameItem(catIdx, itemIdx, e.target.value)}
                    style={{ fontSize: 12, minHeight: 54 }}
                  />
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 11,
                      color: 'var(--muted-foreground)',
                      marginTop: 4,
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={item.allowPhoto}
                      onChange={(e) => toggleItemPhoto(catIdx, itemIdx, e.target.checked)}
                      style={{ width: 14, height: 14, accentColor: 'var(--info)' }}
                    />
                    {t('answerEditor.battery.photoToggle')}
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => removeItem(catIdx, itemIdx)}
                  style={{
                    padding: '2px 6px',
                    background: 'var(--muted)',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 11,
                    borderRadius: 4,
                    color: 'var(--destructive)',
                    height: 28,
                  }}
                  aria-label={t('answerEditor.itemDeleteAria')}
                >
                  {t('answerEditor.battery.remove')}
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-sm btn-default"
              onClick={() => addItem(catIdx)}
              style={{ padding: '4px 10px', fontSize: 12 }}
            >
              <Plus size={14} /> {t('answerEditor.battery.addItem')}
            </button>
          </div>
        </details>
      ))}

      <div style={{ marginTop: 8, fontSize: 'var(--font-size-xs)', color: 'var(--muted-foreground)' }}>
        {t('answerEditor.battery.help')}
      </div>
    </>
  );
}

// ── Editor: MULTI_CHOICE selection bounds (SHCT-240 BR-06) ───────────────

function MultiSelectSettings({
  cfg,
  setCfg,
}: {
  cfg: MultiSelectConfig;
  setCfg: (next: MultiSelectConfig) => void;
}) {
  const { t } = useTranslation('questionnaire-design');
  return (
    <div
      style={{
        marginBottom: 'var(--space-3)',
        padding: 'var(--space-2) var(--space-3)',
        border: '1px dashed var(--border)',
        borderRadius: 'var(--radius-sm)',
        background: '#f8fbff',
      }}
    >
      <div className="form-field" style={{ marginBottom: cfg.mode === 'none' ? 0 : 'var(--space-3)' }}>
        <label className="form-field__label" style={{ fontSize: 11 }}>
          {t('answerEditor.multi.mode')}
        </label>
        <select
          className="form-field__select"
          value={cfg.mode}
          onChange={(e) => {
            const mode = e.target.value as MultiSelectConfig['mode'];
            // 개수 제한(count) fixes min to 1 (PRD §3.3).
            setCfg({ ...cfg, mode, min: mode === 'count' ? 1 : cfg.min });
          }}
          style={{ height: 30, fontSize: 12 }}
        >
          <option value="none">{t('answerEditor.multi.modeNone')}</option>
          <option value="range">{t('answerEditor.multi.modeRange')}</option>
          <option value="count">{t('answerEditor.multi.modeCount')}</option>
        </select>
      </div>
      {cfg.mode !== 'none' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          {cfg.mode === 'range' ? (
            <div className="form-field">
              <label className="form-field__label" style={{ fontSize: 11 }}>
                {t('answerEditor.multi.min')}
              </label>
              <NumberInput
                className="form-field__input"
                value={cfg.min}
                min={1}
                emptyValue={1}
                onChange={(min) => setCfg({ ...cfg, min })}
                style={{ height: 30, fontSize: 12 }}
              />
            </div>
          ) : null}
          <div className="form-field">
            <label className="form-field__label" style={{ fontSize: 11 }}>
              {t('answerEditor.multi.max')}
            </label>
            <NumberInput
              className="form-field__input"
              value={cfg.max}
              min={cfg.mode === 'count' ? 1 : cfg.min}
              emptyValue={1}
              onChange={(max) => setCfg({ ...cfg, max })}
              style={{ height: 30, fontSize: 12 }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ── Dispatcher ──────────────────────────────────────────────────────────

interface AnswerEditorProps {
  answerType: AnswerType;
  choices?: Choice[];
  setChoices: (next: Choice[]) => void;
  multiConfig?: MultiSelectConfig;
  setMultiConfig: (next: MultiSelectConfig) => void;
  textConfig?: TextConfig;
  setTextConfig: (next: TextConfig) => void;
  sliderConfig?: SliderConfig;
  setSliderConfig: (next: SliderConfig) => void;
  photoConfig?: PhotoConfig;
  setPhotoConfig: (next: PhotoConfig) => void;
  batteryConfig?: BatteryConfig;
  setBatteryConfig: (next: BatteryConfig) => void;
  jumpTargets: JumpTargetOption[];
  scoreOutOfBand: ScoreOutOfBand;
}

export function AnswerEditor({
  answerType,
  choices,
  setChoices,
  multiConfig,
  setMultiConfig,
  textConfig,
  setTextConfig,
  sliderConfig,
  setSliderConfig,
  photoConfig,
  setPhotoConfig,
  batteryConfig,
  setBatteryConfig,
  jumpTargets,
  scoreOutOfBand,
}: AnswerEditorProps) {
  switch (answerType) {
    case 'SHORT_ANSWER':
      return (
        <ChoiceTableEditor
          choices={choices ?? []}
          setChoices={setChoices}
          showBranch
          fixedText={false}
          allowOther
          jumpTargets={jumpTargets}
          scoreOutOfBand={scoreOutOfBand}
        />
      );
    case 'MULTI_CHOICE':
      return (
        <>
          <MultiSelectSettings
            cfg={multiConfig ?? defaultMultiSelectConfig()}
            setCfg={setMultiConfig}
          />
          <ChoiceTableEditor
            choices={choices ?? []}
            setChoices={setChoices}
            showBranch={false}
            fixedText={false}
            allowOther
            jumpTargets={jumpTargets}
            scoreOutOfBand={scoreOutOfBand}
          />
        </>
      );
    case 'YES_NO':
      return (
        <ChoiceTableEditor
          choices={choices ?? []}
          setChoices={setChoices}
          showBranch
          fixedText
          allowOther={false}
          jumpTargets={jumpTargets}
          scoreOutOfBand={scoreOutOfBand}
        />
      );
    case 'OX':
      return (
        <OxEditor
          choices={choices ?? []}
          setChoices={setChoices}
          scoreOutOfBand={scoreOutOfBand}
        />
      );
    case 'TEXT':
      return (
        <TextEditor
          cfg={textConfig ?? defaultTextConfig()}
          setCfg={setTextConfig}
          scoreOutOfBand={scoreOutOfBand}
        />
      );
    case 'SLIDER':
      return (
        <SliderEditor
          cfg={sliderConfig ?? defaultSliderConfig()}
          setCfg={setSliderConfig}
          scoreOutOfBand={scoreOutOfBand}
        />
      );
    case 'PHOTO':
      return (
        <PhotoEditor cfg={photoConfig ?? defaultPhotoConfig()} setCfg={setPhotoConfig} />
      );
    case 'BATTERY':
      return (
        <BatteryEditor
          cfg={batteryConfig ?? defaultBatteryConfig()}
          setCfg={setBatteryConfig}
          scoreOutOfBand={scoreOutOfBand}
        />
      );
    default:
      return null;
  }
}
